import React, { useMemo } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Box,
  OutlinedInput,
} from '@mui/material';
const PROJECTS_LABEL_ID = 'project-sprint-filter-projects-label';
const SPRINTS_LABEL_ID = 'project-sprint-filter-sprints-label';

interface FilterProps {
  projects: string[];
  selectedProjects: string[];
  sprints: string[];
  selectedSprints: string[];
  onProjectChange: (selected: string[]) => void;
  onSprintChange: (selected: string[]) => void;
  projectData: any[];
  /** Optional map of project key → display name. When provided, menu labels
   *  show the human-readable name while values remain the key. */
  projectNameMap?: Record<string, string>;
}

const ProjectSprintFilter = ({
  projects,
  selectedProjects,
  sprints,
  selectedSprints,
  onProjectChange,
  onSprintChange,
  projectData,
  projectNameMap,
}: FilterProps) => {
  const handleProjectChange = (event: any) => {
    const value = event.target.value;
    const newSelectedProjects = value[value.length - 1] === 'all'
      ? selectedProjects.length === projects.length ? [] : projects
      : value;
    
    // Get available sprints for selected projects
    const availableSprints = getSprintsForProjects(newSelectedProjects);
    
    // Update selected sprints to only include available ones
    const newSelectedSprints = selectedSprints.filter(sprint => 
      availableSprints.includes(sprint)
    );
    
    onProjectChange(newSelectedProjects);
    onSprintChange(newSelectedSprints);
  };

  const handleSprintChange = (event: any) => {
    const value = event.target.value;
    const availableSprints = getSprintsForProjects(selectedProjects);
    
    if (value[value.length - 1] === 'all') {
      onSprintChange(selectedSprints.length === availableSprints.length ? [] : availableSprints);
      return;
    }
    onSprintChange(value);
  };

  const getSprintsForProjects = (selectedProjs: string[]) => {
    const sprintsSet = new Set<string>();
    projectData
      // Match by project_key (when keys are used as values) or project_name (legacy)
      .filter(project =>
        selectedProjs.includes(project.project_key) ||
        selectedProjs.includes(project.project_name)
      )
      .forEach(project => {
        project.sprints.forEach((sprint: any) => {
          sprintsSet.add(sprint.sprint_name);
        });
      });
    return Array.from(sprintsSet);
  };

  const availableSprints = useMemo(() => 
    getSprintsForProjects(selectedProjects),
    [selectedProjects, projectData]
  );

  return (
    <Box display="flex" gap={2}>
      <FormControl sx={{ minWidth: 200 }}>
        <InputLabel id={PROJECTS_LABEL_ID}>Projects</InputLabel>
        <Select
          labelId={PROJECTS_LABEL_ID}
          multiple
          value={selectedProjects}
          onChange={handleProjectChange}
          input={<OutlinedInput label="Projects" />}
          renderValue={(selected) => `Selected Projects (${selected.length})`}
        >
          <MenuItem value="all">
            <Checkbox checked={selectedProjects.length === projects.length} />
            <ListItemText primary="Select All" />
          </MenuItem>
          {projects.map((project) => (
            <MenuItem key={project} value={project}>
              <Checkbox checked={selectedProjects.indexOf(project) > -1} />
              <ListItemText primary={projectNameMap?.[project] ?? project} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl sx={{ minWidth: 200 }}>
        <InputLabel id={SPRINTS_LABEL_ID}>Sprints</InputLabel>
        <Select
          labelId={SPRINTS_LABEL_ID}
          multiple
          value={selectedSprints}
          onChange={handleSprintChange}
          input={<OutlinedInput label="Sprints" />}
          renderValue={(selected) => `Selected Sprints (${selected.length})`}
        >
          <MenuItem value="all">
            <Checkbox checked={selectedSprints.length === availableSprints.length} />
            <ListItemText primary="Select All" />
          </MenuItem>
          {availableSprints.map((sprint) => (
            <MenuItem key={sprint} value={sprint}>
              <Checkbox checked={selectedSprints.indexOf(sprint) > -1} />
              <ListItemText primary={sprint} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

export default ProjectSprintFilter;